import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {login, logout} from "./authSlice";
import {gecerliKullaniciId, gorunumOku} from "../../core/gorunum/gorunumTercihi";

export interface routerTabsModel {
    patch:string,
    title:string,
    defaultTitle:string,
    keyPath:string[],
    formid:string,
    dynamicReportsId?:number,
    /** sabitlenmiş sekme: hep öndedir (Anasayfa'nın ardından), "Tümünü / Diğerlerini / Sağdakileri kapat" ile kapanmaz,
     *  "Açılışta son sekmeler" kapalıyken de geri gelir. Yalnız tarayıcıda (kullanıcıya bağlı anahtar), sunucuya gitmez */
    sabit?: boolean,
    /** açıldığı an (Date.now): sekme sınırında "en eskiyi kapat" buna bakar; eski kayıtlarda yok (en eski sayılır) */
    acilma?: number,
}

interface commonModel {
    pathname: string,
    routerTabs: routerTabsModel[]
    activeMenuTab:string,
    openTabCount: number,
    unreadMailCount:number,
    lastUserMessageId:number,
    /** açık sekmeleri tarayıcıda saklanan kullanıcı (`routerTabs.<id>` / `activeMenuTab.<id>`); yoksa sekmeler saklanmaz */
    sekmeKullaniciId?: number,
    /** yeni oturum başladı, Görünüm › açılış ekranı henüz uygulanmadı (MenuTabs uygular; F5'te / organizasyon değişiminde false) */
    acilisBekliyor: boolean,
}

// Açık sekmeler kullanıcıya bağlı saklanır: paylaşılan bilgisayarda sonraki kullanıcı öncekinin sekmelerini görmez
const SEKMELER = 'routerTabs';
const ETKIN_SEKME = 'activeMenuTab';
const ANASAYFA = 'main';
/** sessionStorage: bu tarayıcı sekmesinde süren oturumun kullanıcısı (oturumda sekme ilk yazılınca konur). Sayfa yenilenince
 *  (F5, organizasyon değişimi) oturum sürer ve bu oturumun sekmeleri geri gelir; yeni açılışta / girişte Görünüm › "Açılışta
 *  son sekmeler geri gelsin" belirler (kapalıyken açılmayan eski sekmeler F5'te de gelmez) */
const OTURUM = 'mino.sekmeler.oturum';

const depoOku = (depo: () => Storage, anahtar: string): string | null => {
    try {
        return depo().getItem(anahtar);
    } catch {
        return null;
    }
};
const depoYaz = (depo: () => Storage, anahtar: string, deger: string | null) => {
    try {
        if (deger === null) {
            depo().removeItem(anahtar);
        } else {
            depo().setItem(anahtar, deger);
        }
    } catch {
        // saklanamazsa sekmeler oturum içinde yine çalışır
    }
};
/** sabit sekmeler öne, kendi aralarındaki ve ötekilerin sırası korunur (sekme listesine her yazımda uygulanır) */
export const sabitlerOnde = (sekmeler: routerTabsModel[]): routerTabsModel[] => {
    const ilkSabitOlmayan = sekmeler.findIndex(sekme => !sekme.sabit);
    if (ilkSabitOlmayan === -1 || !sekmeler.slice(ilkSabitOlmayan).some(sekme => sekme.sabit)) {
        return sekmeler; // zaten sıralı: aynı dizi (gereksiz yeniden çizim yok)
    }
    return [...sekmeler.filter(sekme => sekme.sabit), ...sekmeler.filter(sekme => !sekme.sabit)];
};

const yerel = () => localStorage;
const oturum = () => sessionStorage;

/** Tek seferlik göç: sekmeler eskiden kullanıcıdan bağımsız `routerTabs` / `activeMenuTab` anahtarlarındaydı. Sahibi belliyse
 *  ve kendi anahtarı yoksa ona taşınır; genel anahtarlar her durumda silinir. */
const genelSekmeleriTasi = (kullaniciId: number, sahibi: boolean) => {
    const sekmeler = depoOku(yerel, SEKMELER);
    const etkin = depoOku(yerel, ETKIN_SEKME);
    if (sekmeler === null && etkin === null) {
        return;
    }
    if (sahibi && depoOku(yerel, `${SEKMELER}.${kullaniciId}`) === null) {
        depoYaz(yerel, `${SEKMELER}.${kullaniciId}`, sekmeler);
        depoYaz(yerel, `${ETKIN_SEKME}.${kullaniciId}`, etkin);
    }
    depoYaz(yerel, SEKMELER, null);
    depoYaz(yerel, ETKIN_SEKME, null);
};

/** kullanıcının saklı sekmeleri; oturum sürmüyorsa ve "Açılışta son sekmeler geri gelsin" kapalıysa yalnız sabitlenmiş
 *  sekmeler (yoksa yalnız Anasayfa) ve etkin sekme Anasayfa */
const sekmeleriOku = (kullaniciId: number | undefined, oturumSurer: boolean): Pick<commonModel, 'routerTabs' | 'activeMenuTab'> => {
    if (!kullaniciId) {
        return {routerTabs: [], activeMenuTab: ANASAYFA};
    }
    const hepsi = oturumSurer || gorunumOku(kullaniciId).sekmeleriGeriYukle;
    let routerTabs: routerTabsModel[] = [];
    try {
        const kayit = JSON.parse(depoOku(yerel, `${SEKMELER}.${kullaniciId}`) ?? '[]');
        // bozuk öğe (patch'siz) atılır: MenuTabs her öğenin patch'ini okur
        routerTabs = Array.isArray(kayit) ? kayit.filter(sekme => typeof sekme?.patch === 'string') : [];
    } catch {
        // bozuk kayıt: Anasayfa ile başlanır
    }
    routerTabs = sabitlerOnde(routerTabs);
    if (!hepsi) {
        return {routerTabs: routerTabs.filter(sekme => sekme.sabit), activeMenuTab: ANASAYFA};
    }
    const etkin = depoOku(yerel, `${ETKIN_SEKME}.${kullaniciId}`);
    return {routerTabs, activeMenuTab: etkin && routerTabs.some(sekme => sekme.patch === etkin) ? etkin : ANASAYFA};
};

/** sekmeler yalnız sekmeleri yüklü kullanıcının anahtarına yazılır; oturum işareti de ilk yazımda konur */
const sekmeleriYaz = (kullaniciId: number | undefined, routerTabs: routerTabsModel[] | undefined) => {
    if (kullaniciId) {
        depoYaz(yerel, `${SEKMELER}.${kullaniciId}`, JSON.stringify(routerTabs || []));
        depoYaz(oturum, OTURUM, String(kullaniciId));
    }
};
const etkinSekmeyiYaz = (kullaniciId: number | undefined, activeMenuTab: string | undefined) => {
    if (kullaniciId) {
        depoYaz(yerel, `${ETKIN_SEKME}.${kullaniciId}`, activeMenuTab || ANASAYFA);
        depoYaz(oturum, OTURUM, String(kullaniciId));
    }
};

/** giriş / çıkış / kullanıcı değişimi: o kullanıcının sekmeleri yüklenir, yeni oturum başlar */
const kullaniciSekmeleri = (state: commonModel, kullaniciId: number | undefined): commonModel | undefined => {
    if (state.sekmeKullaniciId === kullaniciId) {
        return; // aynı kullanıcı (organizasyon değişimi): açık sekmeler olduğu gibi kalır
    }
    depoYaz(oturum, OTURUM, null);
    return {...state, ...sekmeleriOku(kullaniciId, false), sekmeKullaniciId: kullaniciId, acilisBekliyor: !!kullaniciId};
};

// Token yoksa giriş ekranı açılır, sekmeler girişte yüklenir (giriş ekranındaki sekme temizliği önceki kullanıcının kaydına dokunmaz)
const ilkKullaniciId = depoOku(yerel, 'token') ? gecerliKullaniciId() || undefined : undefined;
// girişsiz açılışta genel anahtarların sahibi son giren kullanıcıdır (`lastUser` çıkışta silinmez): girişte adı tutarsa ona taşınır
const sonKullaniciAdi = depoOku(yerel, 'lastUser');
if (ilkKullaniciId) {
    genelSekmeleriTasi(ilkKullaniciId, true);
}
const ilkOturumSurer = !!ilkKullaniciId && depoOku(oturum, OTURUM) === String(ilkKullaniciId);
if (!ilkOturumSurer) {
    depoYaz(oturum, OTURUM, null);
}

const initialState: commonModel = {
    pathname: '/',
    ...sekmeleriOku(ilkKullaniciId, ilkOturumSurer),
    sekmeKullaniciId: ilkKullaniciId,
    acilisBekliyor: !!ilkKullaniciId && !ilkOturumSurer,
    unreadMailCount:0,
    lastUserMessageId:0,
    openTabCount: 30,
};

export const commonSlice = createSlice({
    name: "common",
    initialState: initialState,
    reducers: {
        setRouterTabsActiveKey: (state, action: PayloadAction<{routerTabs:routerTabsModel[], activeMenuTab: string}>) => {
            const routerTabs = sabitlerOnde(action.payload.routerTabs);
            sekmeleriYaz(state.sekmeKullaniciId, routerTabs);
            etkinSekmeyiYaz(state.sekmeKullaniciId, action.payload.activeMenuTab);

            return {
                ...state,
                ...action.payload,
                routerTabs,
            };
        },
        setRouterTabs: (state, action: PayloadAction<routerTabsModel[]>) => {
            const routerTabs = sabitlerOnde(action.payload);
            sekmeleriYaz(state.sekmeKullaniciId, routerTabs);
            state.routerTabs = routerTabs
        },
        /** sekmeyi sabitler / sabitlemeyi kaldırır; sabit sekmeler öne alınır (kaldırılan, sabitlerin hemen ardına düşer) */
        sekmeSabitle: (state, action: PayloadAction<{patch: string, sabit: boolean}>) => {
            const {patch, sabit} = action.payload;
            const i = state.routerTabs.findIndex(sekme => sekme.patch === patch);
            if (i === -1 || !!state.routerTabs[i].sabit === sabit) {
                return;
            }
            const sekme: routerTabsModel = {...state.routerTabs[i]};
            if (sabit) {
                sekme.sabit = true;
            } else {
                delete sekme.sabit;
            }
            const digerleri = state.routerTabs.filter((_, j) => j !== i);
            const sabitSayisi = digerleri.filter(s => s.sabit).length;
            // sabitlenen sabitlerin sonuna, kaldırılan sabitlerin hemen ardına
            const routerTabs = [...digerleri.slice(0, sabitSayisi), sekme, ...digerleri.slice(sabitSayisi)];
            sekmeleriYaz(state.sekmeKullaniciId, routerTabs);
            state.routerTabs = routerTabs;
        },
        /** açılış ekranı uygulandı (ya da uygulanacak bir şey yok): bu oturumda bir daha bakılmaz */
        acilisUygulandi: state => {
            state.acilisBekliyor = false;
        },
        /** kullanıcı değişimi (giriş / çıkış bunu auth `login` / `logout` ile kendiliğinden yapar, aşağıda) */
        kullaniciSekmeleriniYukle: (state, action: PayloadAction<number | undefined>) =>
            kullaniciSekmeleri(state, action.payload || undefined),
        setActiveTabKey: (state, action: PayloadAction<string>) => {
            etkinSekmeyiYaz(state.sekmeKullaniciId, action.payload);
            return  {
                ...state,
                activeMenuTab: action.payload
            };
        },
        setUnreadMailCount: (state, action: PayloadAction<number>) => {
            return  {
                ...state,
                unreadMailCount: action.payload
            };
        },
        setLastUserMessageId: (state, action: PayloadAction<number>) => {
            state.lastUserMessageId = action.payload;
            return state;
           /* return  {
                ...state,
                lastUserMessageId: action.payload
            };*/
        },
    },
    extraReducers: builder => {
        builder
            // giriş; organizasyon değişimi de bu eylemle gelir, kullanıcı aynıysa sekmeler kalır
            .addCase(login, (state, action) => {
                const kullanici = action.payload?.user;
                if (kullanici?.userId) {
                    genelSekmeleriTasi(kullanici.userId, kullanici.username === sonKullaniciAdi);
                }
                return kullaniciSekmeleri(state, kullanici?.userId || undefined);
            })
            // çıkış: sekmeler bellekten kalkar, kullanıcının tarayıcıdaki kaydı durur
            .addCase(logout, state => kullaniciSekmeleri(state, undefined));
    }
})

export const { setRouterTabs, setActiveTabKey, setLastUserMessageId,setUnreadMailCount,setRouterTabsActiveKey,kullaniciSekmeleriniYukle,
    sekmeSabitle, acilisUygulandi } = commonSlice.actions;

export default commonSlice.reducer;
